import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames/bind';
import { useMediaQuery } from "../hooks";
import { ClickOutside } from "../internal/ClickOutside";
import { getMethodsForTestId } from "../utils/getMethodsForTestId";
import getScrollbarWidth from "../utils/getScrollbarWidth";
import noop from "../utils/noop";
import { alignments } from './constants';
import styles from './Popover.css';
var cx = classNames.bind(styles);
var initialPosition = {
  left: null,
  height: null
};

var computedPositionRef = function computedPositionRef(nodeRef) {
  var gap = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;

  var _nodeRef$current$getB = nodeRef.current.getBoundingClientRect(),
      nodeTop = _nodeRef$current$getB.top,
      nodeLeft = _nodeRef$current$getB.left;

  var nodeWidth = nodeRef.current.offsetWidth;
  var nodeHeight = nodeRef.current.offsetHeight;
  var windowHeight = window.innerHeight;
  var nodeHeightWithOffset = nodeHeight + nodeTop + gap;
  var scrollWidth = getScrollbarWidth(); // Необходимо завязывать на документ, так как при зуме изменяется именно он, а не размер окна

  var lessWidth = document.documentElement.clientWidth - (nodeLeft + nodeWidth);
  var left = lessWidth < nodeWidth ? lessWidth - scrollWidth - 20 : null;
  var height = windowHeight < nodeHeightWithOffset ? windowHeight - nodeTop - gap : null;
  return {
    left: left,
    height: height
  };
};

var defaultClasses = {};

var Popover = function Popover(_ref) {
  var children = _ref.children,
      gap = _ref.gap,
      toggler = _ref.toggler,
      _ref$wrapInButton = _ref.wrapInButton,
      wrapInButton = _ref$wrapInButton === void 0 ? true : _ref$wrapInButton,
      _ref$onToggle = _ref.onToggle,
      onToggle = _ref$onToggle === void 0 ? noop : _ref$onToggle,
      isOpen = _ref.isOpen,
      _ref$align = _ref.align,
      align = _ref$align === void 0 ? 'left' : _ref$align,
      isMobileFixed = _ref.isMobileFixed,
      namespace = _ref.namespace,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultClasses : _ref$classes;

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'popover'),
      getTestId = _getMethodsForTestId.getTestId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isOpenState = _useState2[0],
      setIsOpenState = _useState2[1];

  var _useState3 = useState(initialPosition),
      _useState4 = _slicedToArray(_useState3, 2),
      position = _useState4[0],
      setPosition = _useState4[1];

  var popupRef = useRef();
  var buttonRef = useRef();
  var breakpoints = useMediaQuery('xs', 'lg', 'mdMax');
  var isMobile = breakpoints.xs;
  var currentState = isOpen !== undefined ? isOpen : isOpenState;

  var handleToggleOpen = function handleToggleOpen() {
    setIsOpenState(!currentState);

    if (onToggle) {
      onToggle(!currentState);
    }
  };

  var handleCloseOpen = useCallback(function () {
    setIsOpenState(false);

    if (onToggle) {
      onToggle(false);
    }
  }, [onToggle]);
  var ignoreRefs = useMemo(function () {
    return [buttonRef];
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: cx('Popover', classes.root, {
      isMobileFixed: isMobileFixed
    })
  }, wrapInButton ? /*#__PURE__*/React.createElement("button", _extends({
    ref: buttonRef,
    type: "button",
    onClick: handleToggleOpen,
    className: cx('BtnContainer', classes.button)
  }, getTestId()), toggler) : /*#__PURE__*/React.createElement("div", {
    ref: buttonRef
  }, toggler), /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: cx('Popup'),
    in: currentState,
    nodeRef: popupRef,
    timeout: 300,
    onEnter: function onEnter() {
      return setPosition(computedPositionRef(popupRef, gap));
    },
    onExited: function onExited() {
      return setPosition(initialPosition);
    },
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(ClickOutside, {
    ignoreRefs: ignoreRefs,
    onClickOutside: handleCloseOpen
  }, /*#__PURE__*/React.createElement("div", _extends({
    ref: popupRef,
    className: cx('Popup', "Popup-align-".concat(align), classes.popup),
    style: _objectSpread({
      left: align === alignments.left ? position.left : undefined
    }, !(isMobile && isMobileFixed) ? {
      top: "calc(100% + ".concat(gap, "px)")
    } : {})
  }, getTestId('popup')), typeof children === 'function' ? children({
    height: position.height
  }) : children))));
};

export default Popover;